'use strict';

/**
 * Post-Processing Script. to apply one or more graphical effects like Depth-Of-Field, Bloom, Film Grain or various types of Anti-aliasing
 */

/*
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { settingsData, threeJsVariables} from "../../settings";
import {highlightsElements} from "./highlighting-effectFXAA";
import {ambientOcclusion} from "./ambientOcclusion";
import {depthOfFields} from "./depthOfFields";
import {depthOfFields2} from "./depthOfFields2";
*/
import {
	WebGLRenderer,
	PerspectiveCamera,
	Scene,
	DirectionalLight,
	AmbientLight,
	BoxGeometry,
	Mesh,
	PlaneGeometry,
	DoubleSide,
	Group,
	Object3D,
	NearestFilter,
	LinearFilter,
	RGBFormat,
	RGBAFormat,
	OrthographicCamera,
	BufferGeometry,
	BufferAttribute,
	ShaderMaterial,
	WebGLRenderTarget,
	LinearEncoding,
	sRGBEncoding,
	CanvasTexture,
	TextureLoader,
	Color,
	Vector2,
	Vector3,
    
    
    NoBlending,
    NormalBlending,
    AdditiveBlending,
    SubtractiveBlending,
    MultiplyBlending,
    CustomBlending,

	Float32BufferAttribute,
	Uint16BufferAttribute,
	MeshBasicMaterial,
	MeshPhongMaterial,
	MeshStandardMaterial,
	EquirectangularReflectionMapping,
	PCFSoftShadowMap,
	CameraHelper,
	RepeatWrapping,
	MeshPhysicalMaterial,

	NoToneMapping,
	LinearToneMapping,
	ReinhardToneMapping,
	CineonToneMapping,
	ACESFilmicToneMapping,
	FogExp2,
    DepthTexture,
    DepthFormat,
    UnsignedShortType,
    DepthStencilFormat,
    UnsignedInt248Type,
    RawShaderMaterial,
	WebGLMultisampleRenderTarget,
	FloatType,
	UnsignedIntType,
	Matrix4,
} from 'three'

let renderer = null
let camera   = null
let canvas   = null
let scene    = null
let dirLight = null

let WIDTH    = 64
let HEIGHT   = 64
let aspect   = 1.0

let rt1 = null
let rt2 = null
let rt3 = null

let postprocessing_active = false

let bokeh_active   = false
let bokeh_focus    = 80
let bokeh_aperture = 0.5/10000.0
let bokeh_maxblur  = 0.005

let trans_active   = false
let trans_scale    = 0.0
let trans_opacity  = 0.0

const _vector2 = new Vector2()

const orto_camera = new OrthographicCamera(-1, 1, 1, -1, 0, 1)

const pass_geometry = new BufferGeometry()
pass_geometry.setAttribute('position', new BufferAttribute(new Float32Array([-1, -1, 0, -1, 1, 0, 1, -1, 0, 1, -1, 0, -1, 1, 0, 1, 1, 0]), 3))
pass_geometry.setAttribute('uv', new BufferAttribute(new Float32Array([0, 0, 0, 1, 1, 0, 1, 0, 0, 1, 1, 1]), 2))

/**
 * Depth-of-field shader with bokeh
 * ported from GLSL shader by Martins Upitis
 * http://artmartinsh.blogspot.com/2010/02/glsl-lens-blur-filter-with-bokeh.html
 */
const Bokeh = new Mesh(pass_geometry,new ShaderMaterial({
    uniforms: {
        tColor  : { value: null   },
        tDepth  : { value: null   },
        focus   : { value: 1.0    },
        aspect  : { value: 1.0    },
        aperture: { value: 0.025  },
        maxblur : { value: 0.01   },
        far     : { value: 4000   },
    },
    vertexShader:[
        'varying vec2 vUv;',
        'void main() {',
            'vUv = uv;',
            'gl_Position = vec4( position, 1.0 );',
        '}'].join(''),
    fragmentShader:`
        varying vec2 vUv;
        uniform sampler2D tColor;
        uniform sampler2D tDepth;
        uniform float maxblur; // max blur amount
        uniform float aperture; // aperture - bigger values for shallower depth of field
        uniform float focus;
        uniform float aspect;
        uniform float far;
        float perspectiveDepthToViewZ( const in float invClipZ, const in float near, const in float far ) {
            return ( near * far ) / ( ( far - near ) * invClipZ - far );
        }
        void main() {
            vec2 aspectcorrect = vec2( 1.0, aspect );
            float viewZ = perspectiveDepthToViewZ(texture2D( tDepth, vUv).x, 1.0, far );
            float factor = ( focus + viewZ ); // viewZ is <= 0, so this is a difference equation
            vec2 dofblur = vec2 ( clamp( factor * aperture, -maxblur, maxblur ) );
            vec2 dofblur9 = dofblur * 0.9;
            vec2 dofblur7 = dofblur * 0.7;
            vec2 dofblur4 = dofblur * 0.4;
            vec4 col = vec4( 0.0 );
            col += texture2D( tColor, vUv.xy );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.0,   0.4  ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.15,  0.37 ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.29,  0.29 ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.37,  0.15 ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.40,  0.0  ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.37, -0.15 ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.29, -0.29 ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.15, -0.37 ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.0,  -0.4  ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.15,  0.37 ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.29,  0.29 ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.37,  0.15 ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.4,   0.0  ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.37, -0.15 ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.29, -0.29 ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.15, -0.37 ) * aspectcorrect ) * dofblur );

            col += texture2D( tColor, vUv.xy + ( vec2(  0.15,  0.37 ) * aspectcorrect ) * dofblur9 );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.37,  0.15 ) * aspectcorrect ) * dofblur9 );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.37, -0.15 ) * aspectcorrect ) * dofblur9 );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.15, -0.37 ) * aspectcorrect ) * dofblur9 );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.15,  0.37 ) * aspectcorrect ) * dofblur9 );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.37,  0.15 ) * aspectcorrect ) * dofblur9 );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.37, -0.15 ) * aspectcorrect ) * dofblur9 );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.15, -0.37 ) * aspectcorrect ) * dofblur9 );

            col += texture2D( tColor, vUv.xy + ( vec2(  0.29,  0.29 ) * aspectcorrect ) * dofblur7 );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.40,  0.0  ) * aspectcorrect ) * dofblur7 );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.29, -0.29 ) * aspectcorrect ) * dofblur7 );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.0,  -0.4  ) * aspectcorrect ) * dofblur7 );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.29,  0.29 ) * aspectcorrect ) * dofblur7 );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.4,   0.0  ) * aspectcorrect ) * dofblur7 );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.29, -0.29 ) * aspectcorrect ) * dofblur7 );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.0,   0.4  ) * aspectcorrect ) * dofblur7 );

            col += texture2D( tColor, vUv.xy + ( vec2(  0.29,  0.29 ) * aspectcorrect ) * dofblur4 );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.4,   0.0  ) * aspectcorrect ) * dofblur4 );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.29, -0.29 ) * aspectcorrect ) * dofblur4 );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.0,  -0.4  ) * aspectcorrect ) * dofblur4 );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.29,  0.29 ) * aspectcorrect ) * dofblur4 );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.4,   0.0  ) * aspectcorrect ) * dofblur4 );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.29, -0.29 ) * aspectcorrect ) * dofblur4 );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.0,   0.4  ) * aspectcorrect ) * dofblur4 );

            gl_FragColor = col / 41.0;
            gl_FragColor.a = 1.0;
        }`,
    side        : DoubleSide,
    depthTest   : false,
    depthWrite  : false,
    transparent : false,
}))

const layer_mesh_opacity2  = new Mesh(pass_geometry, new RawShaderMaterial({
    uniforms: {
        map     : { value: null },
        scale   : { value: 1.0  },
        opacity : { value: 0.0  },
    },
    vertexShader: [
        'precision highp float;',
        'precision highp int;',
        'attribute vec3 position;',
        'attribute vec2 uv;',
        'varying vec2 lUv;',
        'varying vec2 lp;',
        'void main() {',
            'lUv = uv;',
            'lp.x = position.x;',
            'lp.y = position.y;',
            'gl_Position = vec4(position,1.0);',
        '}'].join(''),
    fragmentShader: [
        'precision highp float;',
        'precision highp int;',
        'uniform sampler2D map;',
        'uniform float opacity;',
        'uniform float scale;',
        'varying vec2 lUv;',
        'varying vec2 lp;',
        'void main(){',
            'vec4 cmap = texture2D(map, lUv);',
            'vec3 color = vec3(0.0);',
            'float total = 0.0;',
            'vec2 toCenter = vec2(0.5, 0.5) - lUv;',
            'for (float t = 0.0; t <= 50.0; t++) {',
                'float percent = t / 50.0;',
                'float weight = 4.0 * (percent - percent * percent);',
                'color += texture2D(map, lUv + toCenter * percent* (0.5-opacity*0.5)).rgb*weight;',
                'total += weight;',
            '}',
            'gl_FragColor = vec4(mix(color/total,vec3(1.0,1.0,1.0),scale), 1.0 );',
        '}'].join(''),
    side       : DoubleSide,
    depthTest  : false,
    depthWrite : false,
    transparent: true,
}))

export const postProcessingResize = (width,height)=>{
    if (WIDTH===width && HEIGHT===height){
        return
    }

    //const dpr = renderer.getPixelRatio()

    WIDTH   = width
    HEIGHT  = height
    aspect  = WIDTH/HEIGHT

    //camera.aspect = aspect
    //camera.updateProjectionMatrix()

    //renderer.setSize( WIDTH, HEIGHT )

    rt1.setSize(WIDTH, HEIGHT)
    rt2.setSize(WIDTH, HEIGHT)
    //rt3.setSize(WIDTH, HEIGHT)

    /*
    // --------- bloom
    let resx = Math.round( WIDTH / 2 )
    let resy = Math.round( HEIGHT / 2 )
  
    bloom_rt.setSize( resx, resy )
    for ( let i=0; i<bloom_nmips; i++ ) {
        bloom_rt_h[i].setSize( resx, resy )
        bloom_rt_v[i].setSize( resx, resy )

        bloom_blur_m[i].material.uniforms.texSize.value.set(resx, resy) 

        resx = Math.round( resx/2 )
        resy = Math.round( resy/2 )
    }
    */

}

export const createRenderTarget = (width,height)=>{
    const rt = new WebGLRenderTarget(
        width, height,
        {
            minFilter     : LinearFilter,
            magFilter     : LinearFilter,
            format        : RGBFormat,
            depthBuffer   : true,
            stencilBuffer : false
        }
    )

    rt.texture.generateMipmaps = false
    rt.depthTexture        = new DepthTexture()
    rt.depthTexture.format = DepthStencilFormat 
    rt.depthTexture.type   = UnsignedInt248Type 
    return rt
}

/**
 * Init Post-Processing and add it to render
 */

 const createRenderTargets = ()=>{

	if (renderer.capabilities.isWebGL2){
		rt1 = new WebGLMultisampleRenderTarget( 
			WIDTH, HEIGHT,
			{
				minFilter     : NearestFilter,
				magFilter     : NearestFilter,
				format        : RGBFormat,
				depthBuffer   : true,
				stencilBuffer : false
			}
		)
		rt1.samples = 4
	
		rt1.texture.generateMipmaps = false
		rt1.depthTexture        = new DepthTexture()
        rt1.depthTexture.format = DepthFormat
		rt1.depthTexture.type   = FloatType //UnsignedIntType //FloatType
	}else{
        
		rt1 = new WebGLRenderTarget(
			WIDTH, HEIGHT,
			{
				minFilter     : NearestFilter,
				magFilter     : NearestFilter,
				format        : RGBFormat,
				depthBuffer   : true,
				stencilBuffer : false
			}
		)

		rt1.texture.generateMipmaps = false
		// настройки для webgl1
		rt1.depthTexture        = new DepthTexture()
    	rt1.depthTexture.format = DepthFormat //DepthStencilFormat 
	    rt1.depthTexture.type   = FloatType //UnsignedShortType //UnsignedInt248Type 
    }


    //--------------------------------------------
    rt2 = new WebGLRenderTarget(
        WIDTH, HEIGHT,
        {
            minFilter     : NearestFilter,
            magFilter     : NearestFilter,
            format        : RGBFormat,
            depthBuffer   : false,
            stencilBuffer : false
        }
    )
    //--------------------------------------------
/*
    rt3 = new WebGLRenderTarget(
        WIDTH, HEIGHT,
        {
            minFilter     : NearestFilter,
            magFilter     : NearestFilter,
            format        : RGBFormat,
            depthBuffer   : false,
            stencilBuffer : false
        }
    )
*/
}

export const isPostProcessingActive = ()=>postprocessing_active

export const postProcessing = (_renderer,_camera,_canvas,_scene,_dirlight = null) => {
    renderer = _renderer
    camera   = _camera
    canvas   = _canvas
    scene    = _scene
	dirLight = _dirlight

    WIDTH   = canvas.width
    HEIGHT  = canvas.height
    aspect  = WIDTH/HEIGHT

    postprocessing_active = true

    createRenderTargets()

    //DOFMipMap.material.uniforms.tColor.value = rt1.texture
    //DOFMipMap.material.uniforms.tDepth.value = rt1.depthTexture

    Bokeh.material.uniforms.tColor.value = rt1.texture
    Bokeh.material.uniforms.tDepth.value = rt1.depthTexture
    Bokeh.material.uniforms.far.value    = camera.far

    //prepareBloom()

    //renderResize()
 
    /*
    const {renderer, scene, perspectiveCamera: camera} = threeJsVariables;
    const {depthOfFields: dof, depthOfFields2: dof2, ambientOcclusion: ao, effectFXAA} = settingsData.postProcessing;

    if (dof || ao || effectFXAA) {
        const composer = new EffectComposer( renderer );
        threeJsVariables.composer = composer;

        const renderPass = new RenderPass( scene, camera );
        composer.addPass( renderPass );

        ao && ambientOcclusion(composer, scene, camera);
        dof && depthOfFields(composer, scene, camera);
        effectFXAA && highlightsElements(composer, scene, camera);
    }

    dof2 && depthOfFields2(scene, camera)
    */
}

export const setTransEffect = (active,scale,opacity)=>{
    trans_active = active
    trans_scale = scale
    trans_opacity = opacity
}


export const renderSceneToRT = (renderer,scene,camera)=>{
    renderer.autoClear = false
    renderer.getSize(_vector2)
    //const rt = createRenderTarget(_u.x*0.25,_u.y*0.25)
    rt2 = createRenderTarget(_vector2.x,_vector2.y)
    
    renderer.setRenderTarget(rt2)
    renderer.render(scene,camera)
}

export const renderTransition = (renderer)=>{
    renderer.setRenderTarget( null )
    layer_mesh_opacity2.material.uniforms.map.value     = rt2.texture
    layer_mesh_opacity2.material.uniforms.opacity.value = trans_opacity
    layer_mesh_opacity2.material.uniforms.scale.value   = trans_scale
    renderer.render(layer_mesh_opacity2, orto_camera)

    rt2.dispose()
    renderer.autoClear = true
}

export const postProcessingRender = ()=>{

    renderer.setRenderTarget( rt1 )

	renderer.render( scene, camera )


    let last_rt = rt1

    //if (bokeh_active){
        if (trans_active) {
            renderer.setRenderTarget( rt2 )
        }else{
            renderer.setRenderTarget( null )
        }
        let y = 50 - camera.position.y
        let d = Math.sqrt(camera.position.x*camera.position.x+y*y+camera.position.z*camera.position.z)
        Bokeh.material.uniforms.focus.value    = d
        Bokeh.material.uniforms.aspect.value   = aspect
        Bokeh.material.uniforms.aperture.value = bokeh_aperture
        Bokeh.material.uniforms.maxblur.value  = bokeh_maxblur
        renderer.render( Bokeh, orto_camera )
        //last_rt = rt3
    //}

    if (trans_active){
        renderer.setRenderTarget( null )
        layer_mesh_opacity2.material.uniforms.map.value     = rt2.texture
        layer_mesh_opacity2.material.uniforms.opacity.value = trans_opacity
        layer_mesh_opacity2.material.uniforms.scale.value   = trans_scale
        renderer.render(layer_mesh_opacity2, orto_camera)
    }

    /*
    if (bloom_active){
        if (bloom_type===0){
            renderBloom(last_rt)
        }
        if (bloom_type===1){
            renderBloom2(last_rt)
        }
        last_rt = rt2
    }

    if (lut_active){
        renderLut(last_rt)
        last_rt = last_rt===rt2?rt3:rt2
    }
    */
    //---------------------------------------

    /*
    renderer.setRenderTarget( null )
    if (fxaa_active && !renderer.capabilities.isWebGL2){
        fxaa_mesh.material.uniforms.tDiffuse.value = last_rt.texture
        fxaa_mesh.material.uniforms.resolution.value.x = 1 / rt1.width
        fxaa_mesh.material.uniforms.resolution.value.y = 1 / rt1.height 
        renderer.render( fxaa_mesh, orto_camera )
    }else{
        screen_copy.material.uniforms.map.value = last_rt.texture
        //screen_copy.material.uniforms.map.value = dirLight.shadow.map.texture
        renderer.render( screen_copy, orto_camera )
    }
    */

}